import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { useOperatorMe, useStaffOrgSelection } from "@/hooks/useOperatorMe";
import { operatorApi, type ApiError, type OperatorMe, type OperatorMeSwitchableOrg } from "@/lib/api";
import { setStaffOrgSelection } from "@/lib/staffOrgSelection";

/**
 * The staff org switch, as one piece of state for the header switcher, the
 * banner and the no-org gate.
 *
 * ## What it is and is not
 *
 * A DigaVouch staff member can view and act in a customer org they are not a
 * member of, for a demo or for support. Whether someone IS staff, and which
 * orgs they may enter, is decided by the backend from server-side data and
 * re-checked on every request; `GET /operator/me` reports it as `staff`. This
 * context only renders what the server said and asks it to switch. Nothing in
 * this file can widen anyone's access: a selection the server refuses is a
 * 403, and `request()` clears it.
 *
 * ## Why a context rather than hooks in the header
 *
 * `useOperatorMe` needs the query client and the auth provider, and the header
 * and shell render in tests (and on public journeys) without them. The
 * provider mounts once in `App.tsx`; `useStaffOrg()` returns `null` anywhere
 * it is absent, so the header and shell render nothing extra and nothing
 * throws.
 *
 * ## What a switch does
 *
 * `switchTo(orgId)` asks the server (which validates the target and records
 * the switch), stores the selection in the cross-subdomain store, drops every
 * cached query (all of them are org-scoped) and goes to `/`, where
 * `OperatorHomeRedirect` lands on the right dashboard for the new org's
 * vertical and `ProtectedRoute` moves to the org's host if it has one.
 */

export interface StaffOrgState {
  /** The server says this person holds an active staff grant. */
  isStaff: boolean;
  /** Viewing an org that is not the person's own. */
  switched: boolean;
  /** The org being viewed while switched (the server's `org`); null at home. */
  viewingOrg: NonNullable<OperatorMe["org"]> | null;
  homeOrgId: string | null;
  /** The home org's name when the server listed it; otherwise null. */
  homeOrgName: string | null;
  switchableOrgs: OperatorMeSwitchableOrg[];
  /** The stored selection (what `X-DigaVouch-Org` carries), or null. */
  selection: string | null;
  switching: boolean;
  /** The last switch attempt's error, in words for the person. */
  error: string | null;
  switchTo: (orgId: string | null) => Promise<void>;
}

const StaffOrgContext = createContext<StaffOrgState | null>(null);

export function StaffOrgProvider({ children }: { children: ReactNode }) {
  const meQuery = useOperatorMe();
  const selection = useStaffOrgSelection();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchTo = useCallback(
    async (orgId: string | null) => {
      setSwitching(true);
      setError(null);
      try {
        const result = await operatorApi.switchOrg(orgId);
        setStaffOrgSelection(result.selection);
        // Every reviewer query is scoped to the org the request acted in.
        // Showing the previous org's rows under the new org's banner, even for
        // a beat, is exactly what the switch must never do.
        queryClient.clear();
        navigate("/", { replace: true });
      } catch (err) {
        const e = err as ApiError;
        setError(
          e.code === "ORG_SELECTION_FORBIDDEN"
            ? "That organization isn’t open to staff in this environment."
            : e.code === "STAFF_ONLY"
              ? "Only DigaVouch staff can switch organizations."
              : "Couldn’t switch organizations. Try again, or sign out and back in.",
        );
      } finally {
        setSwitching(false);
      }
    },
    [navigate, queryClient],
  );

  const value = useMemo<StaffOrgState>(() => {
    const me = meQuery.data;
    const staff = me?.staff ?? null;
    const switched = staff?.switched === true;
    const homeOrgId = staff?.home_org_id ?? null;
    const homeRow = staff?.switchable_orgs.find((o) => o.is_home) ?? null;
    return {
      isStaff: staff !== null,
      switched,
      viewingOrg: switched ? (me?.org ?? null) : null,
      homeOrgId,
      homeOrgName: homeRow?.name ?? (!switched && homeOrgId && me?.org?.org_id === homeOrgId ? me.org.name : null),
      switchableOrgs: staff?.switchable_orgs ?? [],
      selection,
      switching,
      error,
      switchTo,
    };
  }, [meQuery.data, selection, switching, error, switchTo]);

  return <StaffOrgContext.Provider value={value}>{children}</StaffOrgContext.Provider>;
}

/** Null outside the provider (tests, isolated renders): render nothing staff-related. */
export function useStaffOrg(): StaffOrgState | null {
  return useContext(StaffOrgContext);
}
